import { useTranslation } from 'react-i18next'
import { Checkbox } from '../ui/checkbox'
import { Label } from '../ui/label'
import { Input } from '../ui/input'
import { cn } from '../../lib/utils'
import type { ResultTemplateBlock } from '../../types/workout'
import {
  applyWeightToSet,
  applyWeightToTemplate,
  parseWeightInput,
  setCascadeState,
  setRepComplete,
  setRepWeight,
  setSetComplete,
  setTemplateComplete,
  templateCascadeState,
} from '../../utils/workout-result-details'

interface WorkoutResultDetailsEditorProps {
  details: ResultTemplateBlock[]
  onChange: (next: ResultTemplateBlock[]) => void
}

const KIND_BADGE_CLASSES: Record<ResultTemplateBlock['kind'], string> = {
  normal: 'bg-muted text-muted-foreground',
  warmup: 'bg-yellow-100 text-yellow-900',
  dropset: 'bg-purple-100 text-purple-900',
  failure: 'bg-red-100 text-red-900',
}

export function WorkoutResultDetailsEditor({ details, onChange }: WorkoutResultDetailsEditorProps) {
  const { t } = useTranslation()

  if (details.length === 0) return null

  function commitWeightTemplate(templateIdx: number, raw: string) {
    const parsed = parseWeightInput(raw)
    if (parsed === 'invalid') return
    onChange(applyWeightToTemplate(details, templateIdx, parsed))
  }

  function commitWeightSet(templateIdx: number, setIdx: number, raw: string) {
    const parsed = parseWeightInput(raw)
    if (parsed === 'invalid') return
    onChange(applyWeightToSet(details, templateIdx, setIdx, parsed))
  }

  function commitWeightRep(templateIdx: number, setIdx: number, repIdx: number, raw: string) {
    const parsed = parseWeightInput(raw)
    if (parsed === 'invalid') return
    onChange(setRepWeight(details, templateIdx, setIdx, repIdx, parsed))
  }

  return (
    <div className="flex flex-col gap-3">
      <Label>{t('trainingDiary.workouts.result.detailsLabel', 'Sets & reps')}</Label>
      {details.map((block, tIdx) => {
        const tCascade = templateCascadeState(block)
        return (
          <div key={tIdx} className="rounded-md border p-3 bg-background">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Checkbox
                  checked={tCascade === 'checked' ? true : tCascade === 'indeterminate' ? 'indeterminate' : false}
                  onCheckedChange={(v) => onChange(setTemplateComplete(details, tIdx, v === true))}
                  aria-label={t('trainingDiary.workouts.result.markTemplate', 'Mark whole block')}
                />
                <span className="font-medium truncate">{block.exercise_template_title || `#${tIdx + 1}`}</span>
                <span className={cn('text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5', KIND_BADGE_CLASSES[block.kind])}>
                  {block.kind}
                </span>
              </div>
              <WeightInput
                ariaLabel={t('trainingDiary.workouts.result.weightAllRepsHint', 'Weight for all reps')}
                initialValue={null}
                placeholder={t('trainingDiary.workouts.result.weightAllRepsHint', 'Weight for all reps')}
                onCommit={(raw) => commitWeightTemplate(tIdx, raw)}
                className="w-20"
              />
            </div>

            <div className="mt-3 flex flex-col gap-3">
              {block.sets.map((set, sIdx) => {
                const sCascade = setCascadeState(set)
                return (
                  <div key={sIdx} className="rounded border p-2 bg-card">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Checkbox
                          checked={sCascade === 'checked' ? true : sCascade === 'indeterminate' ? 'indeterminate' : false}
                          onCheckedChange={(v) => onChange(setSetComplete(details, tIdx, sIdx, v === true))}
                          aria-label={t('trainingDiary.workouts.result.markSet', 'Mark whole set')}
                        />
                        <span className="text-sm font-medium">
                          {t('trainingDiary.workouts.result.setLabel', 'Set')} {sIdx + 1}
                        </span>
                      </div>
                      <WeightInput
                        ariaLabel={t('trainingDiary.workouts.result.weightAllRepsHint', 'Weight for all reps')}
                        initialValue={null}
                        placeholder="kg"
                        onCommit={(raw) => commitWeightSet(tIdx, sIdx, raw)}
                        className="w-16"
                      />
                    </div>

                    <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {set.reps.map((rep, rIdx) => (
                        <li key={rIdx} className="flex items-center gap-2">
                          <Checkbox
                            checked={rep.complete}
                            onCheckedChange={(v) => onChange(setRepComplete(details, tIdx, sIdx, rIdx, v === true))}
                            aria-label={`${t('trainingDiary.workouts.result.repLabel', 'Rep')} ${rIdx + 1}`}
                          />
                          <span className="text-xs text-muted-foreground w-12">
                            {t('trainingDiary.workouts.result.repLabel', 'Rep')} {rIdx + 1}
                          </span>
                          <WeightInput
                            ariaLabel={`${t('trainingDiary.workouts.result.weightLabel', 'Weight')} (kg)`}
                            initialValue={rep.weight_kg}
                            placeholder="kg"
                            onCommit={(raw) => commitWeightRep(tIdx, sIdx, rIdx, raw)}
                            className="w-16"
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface WeightInputProps {
  initialValue: number | null
  placeholder: string
  ariaLabel: string
  onCommit: (raw: string) => void
  className?: string
}

function WeightInput({ initialValue, placeholder, ariaLabel, onCommit, className }: WeightInputProps) {
  const display = initialValue === null ? '' : String(initialValue)
  return (
    <Input
      key={display}
      type="text"
      inputMode="decimal"
      defaultValue={display}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className={cn('h-8 text-sm', className)}
      onBlur={(e) => onCommit(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          ;(e.currentTarget as HTMLInputElement).blur()
        }
      }}
    />
  )
}
